
/* ============================== src/components/Modals/TaskModal/TaskTimeTab.jsx ============================== */
/**
 * Модуль вкладки учёта времени: прогресс-бар, список логов, форма добавления.
 * Ограничивает ввод дат интервалом [start_date, today]. Экспортирует TaskTimeTab.
 */
import { useState, useMemo } from "react";
import { TaskProgress } from "./TaskProgress";
import { Clickable } from "../../Clickable";
import { Ic } from "../../Icons";
import { ICONS } from "../../iconsData";
import { TODAY, fmtD } from "../../../utils/date";

function LogRow({ log, empName, editing, canLog, onStartEdit, onDelete }) {
  const className = `tm-log${editing ? " tm-log--editing" : ""}`;
  const content = (
    <>
      <span className="tm-log-name">{empName(log.userId)}</span>
      <span className="mut">{fmtD(log.date)}</span>
      <span className="tm-log-note">{log.note}</span>
      <b className="tm-log-h">{log.hours} ч</b>
      {log.updatedBy && log.updatedBy !== log.userId && (
        <span
          className="tm-log-edited"
          title={`Изменил ${empName(log.updatedBy)}`}
        >
          ред.
        </span>
      )}
      {canLog && (
        <button
          type="button"
          className="icon-btn danger xs tm-log-delete"
          title="Удалить запись"
          aria-label="Удалить запись часов"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(log.id);
          }}
        >
          <Ic d={ICONS.trash} size={12} />
        </button>
      )}
    </>
  );

  if (!canLog) return <div className={className}>{content}</div>;

  return (
    <Clickable
      as="div"
      className={className}
      onClick={() => onStartEdit(log)}
      title="Изменить запись"
    >
      {content}
    </Clickable>
  );
}

function sortLogs(logs, field, dir) {
  const arr = [...logs];
  const mul = dir === "asc" ? 1 : -1;
  arr.sort((a, b) => {
    let va;
    let vb;
    if (field === "date") {
      va = a.date;
      vb = b.date;
    } else {
      va = Number(a.hours) || 0;
      vb = Number(b.hours) || 0;
    }
    if (va < vb) return -1 * mul;
    if (va > vb) return 1 * mul;
    if (a.date < b.date) return 1;
    if (a.date > b.date) return -1;
    return 0;
  });
  return arr;
}

export function TaskTimeTab({
  spent,
  planned,
  logs,
  empName,
  canLog,
  slot,
  logDate,
  setLogDate,
  logHours,
  setLogHours,
  logNote,
  setLogNote,
  editingLogId,
  onSave,
  onStartEdit,
  onCancelEdit,
  onDelete,
  taskStartDate,
}) {
  const isEditing = !!editingLogId;
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const sortedLogs = useMemo(
    () => sortLogs(logs, sortField, sortDir),
    [logs, sortField, sortDir],
  );

  const handleSortClick = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const showSortControl = logs.length > 1;

  return (
    <div className="tm-block">
      <TaskProgress value={spent} max={planned || 0} />

      {slot && (
        <div className="tm-logs-head">
          <span className="mut sm">
            План: {slot.startTime}–{slot.endTime} ({planned ?? 0} ч)
            {" · "}
            Факт: {spent} ч
          </span>
        </div>
      )}

      {showSortControl && (
        <div className="tm-logs-head">
          <span className="mut sm">Сортировка:</span>
          <div className="seg sm">
            <button
              type="button"
              className={`seg-btn${sortField === "date" ? " on" : ""}`}
              onClick={() => handleSortClick("date")}
              title="Сортировать по дате"
            >
              По дате{" "}
              {sortField === "date" ? (sortDir === "asc" ? "↑" : "↓") : ""}
            </button>
            <button
              type="button"
              className={`seg-btn${sortField === "hours" ? " on" : ""}`}
              onClick={() => handleSortClick("hours")}
              title="Сортировать по количеству часов"
            >
              По часам{" "}
              {sortField === "hours" ? (sortDir === "asc" ? "↑" : "↓") : ""}
            </button>
          </div>
        </div>
      )}

      {sortedLogs.length > 0 && (
        <div className="tm-logs">
          {sortedLogs.map((l) => (
            <LogRow
              key={l.id}
              log={l}
              empName={empName}
              editing={editingLogId === l.id}
              canLog={canLog}
              onStartEdit={onStartEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {canLog && (
        <form
          className="tm-add"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          noValidate
        >
          <input
            className="inp tm-add-date"
            type="date"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
            min={taskStartDate}
            max={TODAY}
          />
          <input
            className="inp tm-add-hours"
            type="number"
            min="0.5"
            step="0.5"
            placeholder="часы"
            value={logHours}
            onChange={(e) => setLogHours(e.target.value)}
          />
          <input
            className="inp tm-add-note"
            placeholder="комментарий"
            value={logNote}
            onChange={(e) => setLogNote(e.target.value)}
          />
          <button type="submit" className="btn ghost">
            <Ic d={ICONS.clock} size={14} />
            {isEditing ? "Сохранить" : "Внести часы"}
          </button>
          {isEditing && (
            <button type="button" className="btn ghost" onClick={onCancelEdit}>
              Отмена
            </button>
          )}
        </form>
      )}
    </div>
  );
}
